import { Button, Alert } from "react-bootstrap";
import { LinkContainer } from "react-router-bootstrap";
import Card from "react-bootstrap/Card";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faFlag,
    faClockRotateLeft,
    faCircleCheck,
    faTriangleExclamation
} from "@fortawesome/free-solid-svg-icons";

import { GoogleMapsButton } from "./googleMapsButton";
import { Map } from "./map";
import { LocationViewModel } from "../apiClient";
import { formatPostcode } from "../helpers/postcodeHelper";

export const LocationResult = (props: { location: LocationViewModel }) => {
    const { location } = props;
    const now = new Date();
    const updatedDate = location.updatedDate ? new Date(location.updatedDate) : new Date();
    const isOldResult = (now.getTime() - updatedDate.getTime()) > (365 * 24 * 60 * 60 * 1000); // More than 1 year

    const getMachineBadgeClass = (name: string) => {
        const lower = name.toLowerCase();
        if (lower.includes("blade")) return "kf-location-result__badge-tag--blade";
        if (lower.includes("perfectdraft") || lower.includes("perfect draft")) return "kf-location-result__badge-tag--perfectdraft";
        if (lower.includes("sub")) return "kf-location-result__badge-tag--sub";
        return "";
    };

    return (
        <Card className={`kf-location-result ${isOldResult ? "border-danger" : ""}`}>
            <Card.Body className="p-3 p-md-4">
                <Row className="g-4">
                    <Col lg={5} className="kf-location-result__map">
                        {location.coords?.latitude && location.coords?.longitude && (
                            <Map latitude={location.coords.latitude} longitude={location.coords.longitude} />
                        )}
                    </Col>
                    <Col lg={7}>
                        <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-1">
                            <h2 className="kf-location-result__name">{location.name}</h2>
                        </div>
                        <div className="kf-location-result__postcode">
                            {formatPostcode(location.postcode || "")}
                        </div>

                        {/* Machines Badges */}
                        <div className="mb-3">
                            <div className="text-secondary small fw-semibold text-uppercase mb-2">
                                Supported Keg Systems:
                            </div>
                            <div className="d-flex flex-wrap">
                                {location.machines && location.machines.length > 0 ? (
                                    location.machines.map((m) => (
                                        <span
                                            key={m.id || m.name}
                                            className={`kf-location-result__badge-tag ${getMachineBadgeClass(m.name || "")}`}
                                        >
                                            {m.name}
                                        </span>
                                    ))
                                ) : (
                                    <span className="text-muted small">No specific systems listed</span>
                                )}
                            </div>
                        </div>

                        {/* Status / Updated Date */}
                        <div className="kf-location-result__meta mb-3">
                            {isOldResult ? (
                                <span className="text-danger d-inline-flex align-items-center gap-1">
                                    <FontAwesomeIcon icon={faTriangleExclamation} />
                                    <span>Last confirmed: {updatedDate.toLocaleDateString()} (Over 1 year ago)</span>
                                </span>
                            ) : (
                                <span className="text-success d-inline-flex align-items-center gap-1">
                                    <FontAwesomeIcon icon={faCircleCheck} />
                                    <span>Last confirmed: {updatedDate.toLocaleDateString()}</span>
                                </span>
                            )}
                        </div>

                        {isOldResult && (
                            <Alert variant="danger" className="py-2 px-3 small mb-3">
                                <FontAwesomeIcon icon={faClockRotateLeft} className="me-2" />
                                This listing has not been updated recently. We advise contacting the store to confirm stock before traveling.
                            </Alert>
                        )}

                        {location.comment && (
                            <div className="kf-location-result__comment mb-3">
                                &ldquo;{location.comment}&rdquo;
                            </div>
                        )}

                        {/* Actions */}
                        <div className="d-flex flex-wrap gap-2 pt-2">
                            {location.coords?.latitude && location.coords?.longitude && (
                                <GoogleMapsButton
                                    latitude={location.coords.latitude}
                                    longitude={location.coords.longitude}
                                />
                            )}
                            <LinkContainer to={`/report/${location.id}`}>
                                <Button
                                    variant="outline-danger"
                                    className="d-inline-flex align-items-center gap-2"
                                    style={{ borderRadius: "0.65rem", padding: "0.5rem 1rem" }}
                                >
                                    <FontAwesomeIcon icon={faFlag} size="sm" />
                                    <span>Report issue</span>
                                </Button>
                            </LinkContainer>
                        </div>
                    </Col>
                </Row>
            </Card.Body>
        </Card>
    );
};